import {injectable} from 'inversify';

import type {Nullable} from '../../../utils/utility-types';
import type {ICachedMediaDbService} from './types';

import {BlobDBService} from '../../application/BlobDBService';

@injectable()
export class CachedMediaDbService
  extends BlobDBService
  implements ICachedMediaDbService
{
  private objectUrlMap = new Map<string, string>();

  constructor() {
    super({
      dbName: 'ych-cached-media-db',
      dbUsageMaxPercentage: 75,
      loggingPrefix: '[CachedMediaDbService]',
    });
    this.init = this.init.bind(this);
  }

  override async init() {
    await super.init();

    this.log(`Media caching is enabled: ${this.isEnabled}`);
  }

  get isCachingEnabled(): boolean {
    return this.isEnabled;
  }

  override async add(key: string, item: Blob): Promise<string> {
    await super.add(key, item);

    this.objectUrlMap.set(key, URL.createObjectURL(item));

    return key;
  }

  override async clear(): Promise<void> {
    this.objectUrlMap.forEach(url => {
      URL.revokeObjectURL(url);
    });

    this.objectUrlMap.clear();

    await super.clear();
  }

  async getCachedUrl(key: string): Promise<Nullable<string>> {
    let url = this.objectUrlMap.get(key);

    if (url) {
      return url;
    }

    if (!this.isEnabled) {
      return null;
    }

    const blob = await this.get(key);

    if (!(blob instanceof Blob)) {
      return null;
    }

    url = URL.createObjectURL(blob);

    this.objectUrlMap.set(key, url);

    return url;
  }

  override async remove(key: string): Promise<void> {
    const url = this.objectUrlMap.get(key);

    if (url) {
      URL.revokeObjectURL(url);
      this.objectUrlMap.delete(key);
    }

    await super.remove(key);
  }
}
